import React, { useState, useEffect, useCallback } from "react";

const API_BASE_URL = window.API_BASE_URL;

const ACTION_LABELS = {
  GRADE_STARTED: { label: "Grading Started", color: "#3B82F6", icon: "fa-solid fa-play" },
  MARKS_UPDATED: { label: "Marks Updated", color: "#F59E0B", icon: "fa-solid fa-pen" },
  MARKS_DELETED: { label: "Marks Deleted", color: "#EF4444", icon: "fa-solid fa-trash" },
  RESULTS_FINALIZED: { label: "Results Finalized", color: "#10B981", icon: "fa-solid fa-check-circle" },
  RUBRIC_CREATED: { label: "Rubric Created", color: "#8B5CF6", icon: "fa-solid fa-plus" },
  RUBRIC_UPDATED: { label: "Rubric Edited", color: "#A855F7", icon: "fa-solid fa-pen-to-square" },
  RUBRIC_DELETED: { label: "Rubric Deleted", color: "#DC2626", icon: "fa-solid fa-trash-can" },
  RUBRIC_ACTIVATED: { label: "Rubric Activated", color: "#06B6D4", icon: "fa-solid fa-bolt" },
  RUBRIC_GENERATED: { label: "Rubric Generated", color: "#6366F1", icon: "fa-solid fa-wand-magic-sparkles" },
  // Student list actions
  STUDENT_LIST_IMPORTED: { label: "List Imported", color: "#10B981", icon: "fa-solid fa-file-import" },
  STUDENT_LIST_DELETED: { label: "List Deleted", color: "#EF4444", icon: "fa-solid fa-folder-minus" },
  STUDENT_ADDED: { label: "Student Added", color: "#3B82F6", icon: "fa-solid fa-user-plus" },
  STUDENT_DELETED: { label: "Student Deleted", color: "#F97316", icon: "fa-solid fa-user-minus" },
  // Subject rules (rubric marking-rules table)
  SUBJECT_RULES_CREATED: { label: "Subject Rules Created", color: "#3B82F6", icon: "fa-solid fa-list-check" },
  SUBJECT_RULES_UPDATED: { label: "Subject Rules Edited", color: "#F59E0B", icon: "fa-solid fa-pen-to-square" },
  SUBJECT_RULES_DELETED: { label: "Subject Rules Deleted", color: "#DC2626", icon: "fa-solid fa-trash-can" },
  // Scans (image deletions)
  SCAN_DELETED: { label: "Image Deleted", color: "#DC2626", icon: "fa-solid fa-image" },
  // Plans & Prep / Cursor agents
  CURRICULUM_GENERATED: { label: "Plans & Prep Generated", color: "#0EA5E9", icon: "fa-solid fa-book-open" },
};

export default function UserHistory() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]); // [{username, email}]
  const [emailMap, setEmailMap] = useState({}); // {username -> email}
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterUser, setFilterUser] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      const params = new URLSearchParams();
      if (filterUser) params.append('username', filterUser);
      if (filterAction) params.append('action', filterAction);
      if (filterDateFrom) params.append('dateFrom', filterDateFrom);
      if (filterDateTo) params.append('dateTo', filterDateTo);
      params.append('page', page);
      params.append('limit', limit);

      const response = await fetch(`${API_BASE_URL}/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterAction, filterDateFrom, filterDateTo, page]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/audit-logs/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const userList = data.users || [];
        setUsers(userList);
        // Build username → email lookup map
        const map = {};
        userList.forEach(u => { if (u.username) map[u.username] = u.email || ''; });
        setEmailMap(map);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Open the deleted-image preview in a new tab by exchanging the log id
  // for a short-lived signed S3 URL.
  const openDeletedScanPreview = async (logId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/audit-logs/${logId}/scan-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok || !data.downloadUrl) {
        throw new Error(data.message || 'Failed to load image');
      }
      window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening deleted scan preview:', error);
      alert('Could not open image: ' + error.message);
    }
  };

  const handleClearFilters = () => {
    setFilterUser("");
    setFilterAction("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setPage(1);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);

    let relative;
    if (diffMin < 1) relative = "Just now";
    else if (diffMin < 60) relative = `${diffMin}m ago`;
    else if (diffHr < 24) relative = `${diffHr}h ago`;
    else if (diffDays < 7) relative = `${diffDays}d ago`;
    else relative = d.toLocaleDateString();

    return { relative, full: d.toLocaleString() };
  };

  const getActionInfo = (action) => {
    return ACTION_LABELS[action] || { label: action, color: "#6B7280", icon: "fa-solid fa-circle-info" };
  };

  return (
    <div style={{ padding: "16px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
        <div style={{
          width: "40px", height: "40px", borderRadius: "10px",
          background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)"
        }}>
          <i className="fas fa-clock-rotate-left" style={{ color: "white", fontSize: "18px" }}></i>
        </div>
        <div>
          <h1 style={{ color: "var(--text-primary)", margin: 0, fontSize: "1.125rem", fontWeight: "700" }}>
            User History
          </h1>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.75rem" }}>
            Track grading actions, mark changes, Plans & Prep runs, and result finalizations
          </p>
        </div>
        <div style={{ marginLeft: "auto", fontSize: "0.75rem", color: "var(--text-muted)" }}>
          {total} total entries
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px",
        padding: "12px", background: "var(--surface-secondary, #f8f9fa)",
        borderRadius: "8px", border: "1px solid var(--border, #e5e7eb)"
      }}>
        <select
          value={filterUser}
          onChange={(e) => { setFilterUser(e.target.value); setPage(1); }}
          style={{
            padding: "6px 10px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
            fontSize: "12px", background: "var(--surface, white)", color: "var(--text-primary)",
            minWidth: "140px"
          }}
        >
          <option value="">All Users</option>
          {users.map(u => (
            <option key={u.username} value={u.username}>
              {u.username}{u.email ? ` (${u.email})` : ''}
            </option>
          ))}
        </select>

        <select
          value={filterAction}
          onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
          style={{
            padding: "6px 10px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
            fontSize: "12px", background: "var(--surface, white)", color: "var(--text-primary)",
            minWidth: "140px"
          }}
        >
          <option value="">All Actions</option>
          {Object.entries(ACTION_LABELS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        <input
          type="date"
          value={filterDateFrom}
          onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }}
          style={{
            padding: "6px 10px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
            fontSize: "12px", background: "var(--surface, white)", color: "var(--text-primary)"
          }}
          title="From date"
        />

        <input
          type="date"
          value={filterDateTo}
          onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }}
          style={{
            padding: "6px 10px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
            fontSize: "12px", background: "var(--surface, white)", color: "var(--text-primary)"
          }}
          title="To date"
        />

        {(filterUser || filterAction || filterDateFrom || filterDateTo) && (
          <button
            onClick={handleClearFilters}
            style={{
              padding: "6px 12px", borderRadius: "6px", border: "none",
              background: "#EF4444", color: "white", fontSize: "12px",
              cursor: "pointer", fontWeight: 600
            }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Log Entries */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: "24px", marginBottom: "8px" }}></i>
          <p>Loading history...</p>
        </div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
          <i className="fas fa-inbox" style={{ fontSize: "32px", marginBottom: "8px" }}></i>
          <p>No audit logs found</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {logs.map((log) => {
            const actionInfo = getActionInfo(log.action);
            const date = formatDate(log.created_at);
            const oldVal = log.old_value ? (typeof log.old_value === 'string' ? JSON.parse(log.old_value) : log.old_value) : null;
            const newVal = log.new_value ? (typeof log.new_value === 'string' ? JSON.parse(log.new_value) : log.new_value) : null;

            return (
              <div
                key={log.id}
                style={{
                  display: "flex", gap: "12px", padding: "10px 14px",
                  background: "var(--surface, white)", borderRadius: "8px",
                  border: "1px solid var(--border, #e5e7eb)",
                  alignItems: "flex-start"
                }}
              >
                {/* Action Icon */}
                <div style={{
                  width: "32px", height: "32px", borderRadius: "8px",
                  background: `${actionInfo.color}15`, color: actionInfo.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, fontSize: "14px"
                }}>
                  <i className={actionInfo.icon}></i>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{
                      fontWeight: 600, fontSize: "13px", color: "var(--text-primary)"
                    }}>
                      {log.username}
                    </span>
                    {emailMap[log.username] && (
                      <span style={{
                        fontSize: "11px", color: "var(--text-muted, #9ca3af)",
                        display: "flex", alignItems: "center", gap: "3px"
                      }}>
                        <i className="fa-solid fa-envelope" style={{ fontSize: "9px" }}></i>
                        {emailMap[log.username]}
                      </span>
                    )}
                    <span style={{
                      padding: "2px 8px", borderRadius: "4px", fontSize: "11px",
                      fontWeight: 600, background: `${actionInfo.color}15`, color: actionInfo.color
                    }}>
                      {actionInfo.label}
                    </span>
                  </div>

                  {/* Details */}
                  <p style={{
                    margin: "4px 0 0", fontSize: "12px", color: "var(--text-secondary, #6b7280)",
                    lineHeight: 1.4,
                    whiteSpace: log.action === 'CURRICULUM_GENERATED' ? 'pre-wrap' : 'normal',
                    fontFamily: log.action === 'CURRICULUM_GENERATED'
                      ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
                      : 'inherit',
                    maxHeight: log.action === 'CURRICULUM_GENERATED' ? 280 : undefined,
                    overflow: log.action === 'CURRICULUM_GENERATED' ? 'auto' : undefined,
                  }}>
                    {log.details}
                  </p>

                  {log.action === 'CURRICULUM_GENERATED' && newVal && (
                    <div style={{
                      marginTop: "6px", padding: "6px 10px", borderRadius: "6px",
                      background: "var(--surface-secondary, #f8f9fa)", fontSize: "11px",
                      display: "flex", gap: "14px", flexWrap: "wrap"
                    }}>
                      {newVal.start_time && (
                        <span><strong>Start:</strong> {new Date(newVal.start_time).toLocaleString()}</span>
                      )}
                      {newVal.end_time && (
                        <span><strong>End:</strong> {new Date(newVal.end_time).toLocaleString()}</span>
                      )}
                      {(newVal.duration_label || newVal.duration_sec != null) && (
                        <span>
                          <strong>Time taken:</strong>{' '}
                          {newVal.duration_label
                            || (() => {
                              const sec = Math.max(0, Math.floor(Number(newVal.duration_sec) || 0));
                              return `${Math.floor(sec / 60)} min ${String(sec % 60).padStart(2, '0')} sec`;
                            })()}
                        </span>
                      )}
                      {newVal.agents_total != null && (
                        <span><strong>Agents:</strong> {newVal.agents_total}</span>
                      )}
                    </div>
                  )}

                  {/* Show old → new for mark changes */}
                  {log.action === 'MARKS_UPDATED' && oldVal && newVal && (
                    <div style={{
                      marginTop: "6px", padding: "6px 10px", borderRadius: "6px",
                      background: "var(--surface-secondary, #f8f9fa)", fontSize: "11px",
                      display: "flex", gap: "12px", flexWrap: "wrap"
                    }}>
                      <span>
                        <strong>Old:</strong>{" "}
                        <span style={{ color: "#EF4444" }}>{oldVal.marks || "—"}</span>
                      </span>
                      <span>→</span>
                      <span>
                        <strong>New:</strong>{" "}
                        <span style={{ color: "#10B981" }}>{newVal.marks || "—"}</span>
                      </span>
                    </div>
                  )}

                  {/* Show metadata diff for rubric updates */}
                  {log.action === 'RUBRIC_UPDATED' && oldVal && newVal && (
                    <div style={{
                      marginTop: "6px", padding: "6px 10px", borderRadius: "6px",
                      background: "var(--surface-secondary, #f8f9fa)", fontSize: "11px",
                      display: "flex", gap: "12px", flexWrap: "wrap"
                    }}>
                      {oldVal.class !== newVal.class && (
                        <span><strong>Class:</strong> <span style={{ color: "#EF4444" }}>{oldVal.class}</span> → <span style={{ color: "#10B981" }}>{newVal.class}</span></span>
                      )}
                      {oldVal.subject !== newVal.subject && (
                        <span><strong>Subject:</strong> <span style={{ color: "#EF4444" }}>{oldVal.subject}</span> → <span style={{ color: "#10B981" }}>{newVal.subject}</span></span>
                      )}
                      {oldVal.test_date !== newVal.test_date && (
                        <span><strong>Date:</strong> <span style={{ color: "#EF4444" }}>{oldVal.test_date || "—"}</span> → <span style={{ color: "#10B981" }}>{newVal.test_date || "—"}</span></span>
                      )}
                    </div>
                  )}

                  {/* Subject rules — show full table snapshot, plus diff on update */}
                  {['SUBJECT_RULES_CREATED', 'SUBJECT_RULES_UPDATED', 'SUBJECT_RULES_DELETED'].includes(log.action) && (() => {
                    const renderRows = (rows) => {
                      if (!Array.isArray(rows) || rows.length === 0) {
                        return <span style={{ color: 'var(--text-muted, #9ca3af)', fontStyle: 'italic' }}>(no rows)</span>;
                      }
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: 4 }}>
                          <thead>
                            <tr style={{ background: 'rgba(0,0,0,0.04)' }}>
                              <th style={{ padding: '3px 6px', textAlign: 'left', borderBottom: '1px solid rgba(0,0,0,0.08)', width: 60 }}>Marks</th>
                              <th style={{ padding: '3px 6px', textAlign: 'left', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>Rule</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r, i) => (
                              <tr key={i}>
                                <td style={{ padding: '2px 6px', fontFamily: 'monospace' }}>{r.marks ?? '—'}</td>
                                <td style={{ padding: '2px 6px' }}>{r.rule ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    };
                    if (log.action === 'SUBJECT_RULES_UPDATED' && oldVal && newVal) {
                      const sameRules = JSON.stringify(oldVal.rules || []) === JSON.stringify(newVal.rules || []);
                      const sameName = oldVal.display_name === newVal.display_name;
                      return (
                        <div style={{
                          marginTop: 6, padding: '8px 10px', borderRadius: 6,
                          background: 'var(--surface-secondary, #f8f9fa)', fontSize: 11
                        }}>
                          {!sameName && (
                            <div style={{ marginBottom: 6 }}>
                              <strong>Name:</strong>{' '}
                              <span style={{ color: '#EF4444' }}>{oldVal.display_name}</span>{' → '}
                              <span style={{ color: '#10B981' }}>{newVal.display_name}</span>
                            </div>
                          )}
                          {!sameRules && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                              <div>
                                <div style={{ fontWeight: 700, color: '#DC2626', marginBottom: 2 }}>Before</div>
                                {renderRows(oldVal.rules)}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, color: '#059669', marginBottom: 2 }}>After</div>
                                {renderRows(newVal.rules)}
                              </div>
                            </div>
                          )}
                          {sameRules && sameName && (
                            <span style={{ color: 'var(--text-muted, #9ca3af)', fontStyle: 'italic' }}>
                              No row changes recorded
                            </span>
                          )}
                        </div>
                      );
                    }
                    // CREATED / DELETED — single snapshot
                    const snap = newVal || oldVal;
                    if (!snap) return null;
                    return (
                      <div style={{
                        marginTop: 6, padding: '8px 10px', borderRadius: 6,
                        background: 'var(--surface-secondary, #f8f9fa)', fontSize: 11
                      }}>
                        <div style={{ marginBottom: 4 }}>
                          <strong>Name:</strong> {snap.display_name}
                          {snap.subject_key && (
                            <span style={{ marginLeft: 8, color: 'var(--text-muted, #9ca3af)', fontFamily: 'monospace', fontSize: 10 }}>
                              ({snap.subject_key})
                            </span>
                          )}
                        </div>
                        {renderRows(snap.rules)}
                      </div>
                    );
                  })()}

                  {/* Deleted scan image — clickable filename opens the preserved S3 image in a new tab */}
                  {log.action === 'SCAN_DELETED' && oldVal && (
                    <div style={{
                      marginTop: "6px", padding: "8px 10px", borderRadius: "6px",
                      background: "rgba(220, 38, 38, 0.06)",
                      border: "1px solid rgba(220, 38, 38, 0.18)",
                      fontSize: "11px", display: "flex", flexWrap: "wrap",
                      alignItems: "center", gap: "10px"
                    }}>
                      <button
                        onClick={() => openDeletedScanPreview(log.id)}
                        title="Open deleted image in a new tab"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          padding: "4px 10px", borderRadius: "6px",
                          background: "#DC2626", color: "white", border: "none",
                          fontSize: "11px", fontWeight: 600, cursor: "pointer"
                        }}
                      >
                        <i className="fa-solid fa-image"></i>
                        {oldVal.file_name || 'View image'}
                      </button>
                      {oldVal.rubric_class && oldVal.rubric_subject && (
                        <span style={{ color: "var(--text-secondary, #4b5563)" }}>
                          <strong>Folder:</strong> {oldVal.rubric_class} - {oldVal.rubric_subject}
                          {oldVal.rubric_test_date && (
                            <> ({new Date(oldVal.rubric_test_date).toLocaleDateString()})</>
                          )}
                        </span>
                      )}
                      {oldVal.uploaded_by && oldVal.uploaded_by !== log.username && (
                        <span style={{ color: "var(--text-muted, #9ca3af)" }}>
                          Originally uploaded by <strong>{oldVal.uploaded_by}</strong>
                        </span>
                      )}
                    </div>
                  )}

                  {/* Show conceptual mistakes for rubric entries */}
                  {['RUBRIC_CREATED', 'RUBRIC_UPDATED', 'RUBRIC_DELETED'].includes(log.action) && (
                    () => {
                      const issues = (newVal?.issues_text) || (oldVal?.issues_text);
                      if (!issues) return null;
                      return (
                        <div style={{
                          marginTop: "6px", padding: "8px 10px", borderRadius: "6px",
                          background: "rgba(239, 68, 68, 0.06)",
                          border: "1px solid rgba(239, 68, 68, 0.15)",
                          fontSize: "11px", lineHeight: 1.5
                        }}>
                          <div style={{ fontWeight: 600, color: "#DC2626", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "10px" }}></i>
                            Conceptual Mistakes in Paper
                          </div>
                          <div style={{ color: "var(--text-secondary, #4b5563)", whiteSpace: "pre-wrap" }}>
                            {issues}
                          </div>
                        </div>
                      );
                    }
                  )()}
                </div>

                {/* Timestamp */}
                <div style={{
                  flexShrink: 0, textAlign: "right", fontSize: "11px",
                  color: "var(--text-muted, #9ca3af)"
                }}>
                  <div>{date.relative}</div>
                  <div style={{ fontSize: "10px" }}>{date.full}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          gap: "8px", marginTop: "16px"
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            style={{
              padding: "6px 14px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
              background: page <= 1 ? "var(--surface-secondary, #f3f4f6)" : "var(--surface, white)",
              color: page <= 1 ? "var(--text-muted)" : "var(--text-primary)",
              cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: 600
            }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            style={{
              padding: "6px 14px", borderRadius: "6px", border: "1px solid var(--border, #d1d5db)",
              background: page >= totalPages ? "var(--surface-secondary, #f3f4f6)" : "var(--surface, white)",
              color: page >= totalPages ? "var(--text-muted)" : "var(--text-primary)",
              cursor: page >= totalPages ? "not-allowed" : "pointer", fontSize: "12px", fontWeight: 600
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
